import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormError } from "@/components/ui/form-error";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useLogin } from "@/auth/hooks";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const login = useLogin();
  const [authError, setAuthError] = useState<string | null>(null);

  const schema = z.object({
    email: z
      .string({ required_error: t("auth.validation.email_required") })
      .min(1, t("auth.validation.email_required"))
      .email(t("auth.validation.email_invalid")),
    password: z
      .string({ required_error: t("auth.validation.password_required") })
      .min(8, t("auth.validation.password_min")),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  function onSubmit(values: z.infer<typeof schema>): void {
    setAuthError(null);
    login.mutate(values, {
      onSuccess: () => {
        const from = (location.state as LocationState | null)?.from ?? "/dashboard";
        navigate(from, { replace: true });
      },
      onError: (err) => {
        const messageKey = extractErrorKey(err);
        // Bekannte Auth-Errors: inline oberhalb des Formulars zeigen,
        // nicht per Toast in der Ecke (UX-Audit-Punkt 6).
        if (messageKey === "unknown") {
          toast({
            variant: "destructive",
            title: t("common.error"),
            description: t("auth.errors.unknown"),
          });
        } else {
          setAuthError(t(`auth.errors.${messageKey}`));
        }
      },
    });
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4">
      {/* Brand-Background: subtiler radialer Gradient in Primary-Türkis. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.12),transparent_60%),radial-gradient(circle_at_80%_85%,hsl(var(--accent)/0.10),transparent_55%)]"
      />
      <div className="relative w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo size={1.8} />
          <p className="text-sm text-muted-foreground">{t("app.tagline")}</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>{t("auth.login.title")}</CardTitle>
            <CardDescription>{t("auth.login.subtitle")}</CardDescription>
          </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              className="space-y-4"
            >
              <FormError>{authError}</FormError>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.login.email")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="username"
                        placeholder={t("auth.login.email_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.login.password")}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={login.isPending}
              >
                {login.isPending ? t("auth.login.submitting") : t("auth.login.submit")}
              </Button>
            </form>
          </Form>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

function extractErrorKey(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as ApiErrorBody | undefined;
    const code = data?.error?.code;
    if (code === "invalid_credentials") return "invalid_credentials";
    if (code === "user_disabled") return "user_disabled";
    if (code === "user_not_found") return "user_not_found";
  }
  return "unknown";
}
